import { Box } from "@mui/system";
import { CircularProgress } from "@mui/material";
import FilterPill from "../Common/FilterPill";
import { formatDateOnly, getPresetDateRange, NumberFilterValue, toFilterOptions, toOpportunityFilterOptions, toRecordFilterOptions } from "../../utils/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UserTeam from "../Analytics/UserTeam";
import { DateRangeValue, UserInfo, UserTeamData } from "../../utils/types";
import { getUserAttributes } from "../../utils/auth";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import useGlobalContext from "../../context/useGlobalContext";
import ColumnFilter, { ExclusiveColumnFilterContext } from "../Common/customFilter";
import Datatable from "../Common/Datatable";
import getOpportunitiesColumns, { OpportunityFilterOption } from "./opportunitiesTableConfig";
import OpportunitiesService from "../../services/opportunities/OpportunitiesService";
import { OpportunitiesProps, OpportunityColumnVisibility, OpportunityRow, OpportunityNavigationState, CLOSED_DEAL_STAGES } from "../../interfaces/opportunities";
import UserInteractionsService from "../../services/interactions/Interactions";
import SlideOver from "../Common/SlideOver";
import OpportunityDetails from "./OpportunityDetails";
import { useLocation, useNavigate } from "react-router-dom";
import RefreshIcon from "../../assets/common/refresh_icon.svg"

const columnPickerOptions = [
  { value: "opportunityName", label: "Opportunity Name", defaultValue: true },
  { value: "account", label: "Account" },
  { value: "arrAmount", label: "ARR Amount" },
  { value: "opportunityOwner", label: "Opp. Owner" },
  { value: "dealStage", label: "Deal Stage" },
  { value: "closeDate", label: "Closed Date" },
  { value: "nextMeeting", label: "Next Meeting" },
  { value: "managerForecast", label: "Manager Forecast" },
];

const generateRows = (opportunities: any[] = []) =>
  opportunities.map((item) => ({
    id: item?.mappingId,
    opportunityName: item?.opportunityName ?? "",
    account: item?.accountName ?? "",
    arrAmount: item?.arrAmount ?? null,
    opportunityOwner: item?.opportunityOwner ?? "",
    stakeholders: item?.stakeholders ?? null,
    dealStage: item?.dealStage ?? "",
    closeDate: item?.closeDate ?? "",
    nextMeeting: item?.nextInteractionDate ?? "",
    managerForecast: item?.managerForecast ?? "",
    salesforceOpportunityUrl: item?.salesforceOpportunityUrl ?? "",
  }));

const toArrAmountQuery = (filter?: NumberFilterValue) => {
  if (filter?.value === undefined) return {};
  switch (filter.operator) {
    case "gt":
      return { arrAmountMin: filter.value };
    case "lt":
      return { arrAmountMax: filter.value };
    default: {
      const unsupported: never = filter.operator;
      void unsupported;
      return {};
    }
  }
};

const Opportunities: React.FC<OpportunitiesProps> = ({
  setOpportunityDateRange,
  subOrgId,
  dateRange: externalDateRange,
  employeePorId,
  isAccountSlideOver = false,
  accountViewState,
  onHeaderActionsChange,
  sf_account_id,
  startTime,
  endTime,
}) => {
  const {
    accountDetails,
    impersonationDetails,
  } = useGlobalContext();
  const loggedInProfileOrgId = accountDetails?.profileOrgId;
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;
  const initialThreeMonthRange = getPresetDateRange("last3MonthsFull");
  const location = useLocation();

  const [navState, setNavState] = useState<OpportunityNavigationState | null | undefined>(() => {
    if (isAccountSlideOver) {
      return accountViewState?.opportunityNavigationState || null;
    }
    return location.state?.opportunityNavigationState || null;
  });


  const [dateRange, setDateRange] = useState<{
    preset: string;
    startDate: string;
    endDate: string;
  }>(() => {
    if (navState?.dateRange?.startDate && navState?.dateRange?.endDate) {
      return navState.dateRange;
    }
    if (externalDateRange?.startDate && externalDateRange?.endDate) {
      return {
        preset: "custom",
        startDate: externalDateRange.startDate,
        endDate: externalDateRange.endDate,
      };
    }
    return {
      preset: "last3MonthsFull",
      startDate: initialThreeMonthRange.start_time,
      endDate: initialThreeMonthRange.end_time,
    };
  });

  useEffect(() => {
    if (setOpportunityDateRange && dateRange?.startDate && dateRange?.endDate) {
      setOpportunityDateRange(dateRange);
    }
  }, [dateRange, setOpportunityDateRange]);

  const formatDateRange = (start: string, end: string) => {
    if (!start || !end) return "";

    const format = (d: string) =>
      new Date(d).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });

    return `${format(start)} - ${format(end)}`;
  };

  const loggedInEmail = getUserAttributes()?.email;
  const [newUserTeamData, setNewUserTeamData] = useState<UserTeamData | null>(null);
  const handleNewUserTeamData = (data: {
    currentUser: string;
    clickedUserInfo?: UserInfo;
    currentManager?: UserInfo;
    departments: any[];
    subordinates: UserInfo[];
    managerOptions: UserInfo[];
  }) => {
    setNewUserTeamData(data);
  };
  const [selectedUser, setSelectedUser] = useState<any | null>(null); // State for selected user
  const selectedUserPorOrgId =
    selectedUser?.profileOrgId ||
    newUserTeamData?.currentUser ||
    (impersonationDetails?.isImpersonating
        ? impersonationDetails.impersonatingUserPorId
        : loggedInProfileOrgId);
  const impersonatingEmployeeId =
    impersonationDetails?.accessType === "team"
      ? impersonationDetails.impersonatingUserPorId 
      : "";

  const [sort, setSort] = useState<{ sort_field: string; sort_order?: "asc" | "desc" }>(() =>
    navState?.sort || {
      sort_field: "opportunityName",
      sort_order: "asc",
    }
  );
  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0) {
      const sortField = sorting[0].id === "nextMeeting" ? "nextInteractionDate" : sorting[0].id;
      setSort({
        sort_field: sortField,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
    }
  };
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState<number>(() => navState?.pageNumber || 1);
  const [pageSize, setPageSize] = useState<number>(() => navState?.pageSize || 50);
  const [rows, setRows] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [opportunityOptions, setOpportunityOptions] = useState<OpportunityFilterOption[]>([]);
  const [accountOptions, setAccountOptions] = useState<OpportunityFilterOption[]>([]);
  const [opportunityOwnerOptions, setOpportunityOwnerOptions] = useState<OpportunityFilterOption[]>([]);
  const [dealStageOptions, setDealStageOptions] = useState<OpportunityFilterOption[]>([]);
  const [managerForecastOptions, setManagerForecastOptions] = useState<OpportunityFilterOption[]>([]);
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<string[]>(() => navState?.selectedOpportunityIds || []);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(() => navState?.selectedAccountIds || []);
  const [selectedOpportunityOwnerIds, setSelectedOpportunityOwnerIds] = useState<string[]>(() => navState?.selectedOpportunityOwnerIds || []);
  const [selectedDealStages, setSelectedDealStages] = useState<string[]>(() => navState?.selectedDealStages || []);
  const [selectedManagerForecasts, setSelectedManagerForecasts] = useState<string[]>(() => navState?.selectedManagerForecasts || []);
  const [selectedArrAmount, setSelectedArrAmount] = useState<NumberFilterValue | undefined>(() => navState?.selectedArrAmount);
  const [nextMeetingRange, setNextMeetingRange] = useState<DateRangeValue | null>(() => navState?.nextMeetingRange || null);
  const [closeDateRange, setCloseDateRange] = useState<DateRangeValue | null>(() => navState?.closeDateRange || null);
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const isInitialMount = useRef(true);
  const isDealStageInitialized = useRef(false);
  const [isDealStageReady, setIsDealStageReady] = useState(() => Boolean(navState?.selectedDealStages));
  const exclusiveFilterValue = useMemo(
    () => ({
      activeKey: openFilterColumn,
      setActiveKey: setOpenFilterColumn,
    }),
    [openFilterColumn]
  );
  const { showSnackbar } = useDisplaySnackbar();

  const {
    mutate: refreshOpportunities,
    isPending: isRefreshingOpportunities,
  } = OpportunitiesService.useRefreshSalesforceOpportunityMapping({
    onSuccess: (data: any) => {
      showSnackbar(
        "success",
        data?.message || "Opportunities refresh initiated successfully."
      );
    },
    onError: (err: any) => {
      showSnackbar(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to refresh opportunities."
      );
    },
  });

  const isRefreshDisabled = isRefreshingOpportunities || !sf_account_id;

  const handleRefresh = useCallback(() => {
    if (isRefreshDisabled) return;
    refreshOpportunities({
      sf_account_id,
      ...(isImpersonating && impersonatingUserPorId && {
        impersonatingPorId: impersonatingUserPorId,
        ...(accessLevel && { accessLevel }),
      }),
    });
  }, [
    accessLevel,
    impersonatingUserPorId,
    isImpersonating,
    isRefreshDisabled,
    refreshOpportunities,
    sf_account_id,
  ]);

  useEffect(() => {
    if (!onHeaderActionsChange) return;

    const refreshButtonNode = (
      <button
        type="button"
        onClick={handleRefresh}
        disabled={isRefreshDisabled}
        className={`opportunity-refresh-btn ${isRefreshingOpportunities ? "loading" : ""}`}
      >
        {isRefreshingOpportunities ? (
          <CircularProgress size={15} sx={{ color: "#4D515A" }} />
        ) : (
          <span className="opportunity-refresh-icon-placeholder">
            <img src={RefreshIcon} alt="" style={{height: "15px", width: "15px"}} />
          </span>
        )}
        <span className="opportunity-refresh-btn-text">Refresh</span>
      </button>
    );

    onHeaderActionsChange(refreshButtonNode);

    return () => {
      onHeaderActionsChange(null);
    };
  }, [onHeaderActionsChange, handleRefresh, isRefreshingOpportunities]);

  useEffect(() => {
     if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!isAccountSlideOver) {
      setPageNumber(1);
    }
  }, [
    externalDateRange?.startDate,
    externalDateRange?.endDate,
    employeePorId,
    selectedUserPorOrgId,
    sort.sort_field,
    sort.sort_order,
    isAccountSlideOver,
  ]);

  const {
    isLoading: isSubOrgOpportunitiesLoading,
    isFetching: isSubOrgOpportunitiesFetching,
    isSuccess: isSubOrgOpportunitiesSuccess,
    isError: isSubOrgOpportunitiesError,
    error: subOrgOpportunitiesError,
    data: subOrgOpportunitiesData,
  } = OpportunitiesService.useGetSubOrgOpportunities({
    limit: pageSize,
    offset: pageNumber,
    sortOrder: sort.sort_order ?? "asc",
    sortBy: sort.sort_field,
    employeePorId: employeePorId || selectedUserPorOrgId || "",
    closeDateStart: isAccountSlideOver
      ? (closeDateRange?.startDate && closeDateRange?.endDate
          ? formatDateOnly(closeDateRange.startDate)
          : undefined)
      : formatDateOnly(startTime || externalDateRange?.startDate || dateRange.startDate),
    closeDateEnd: isAccountSlideOver
      ? (closeDateRange?.startDate && closeDateRange?.endDate
          ? formatDateOnly(closeDateRange.endDate)
          : undefined)
      : formatDateOnly(endTime || externalDateRange?.endDate || dateRange.endDate),
    impersonatingPorId: impersonatingUserPorId,
    accessLevel: accessLevel,
    opportunityIds: selectedOpportunityIds,
    dealStages: selectedDealStages,
    managerForecasts: selectedManagerForecasts,
    subOrgIds: subOrgId ? [subOrgId] : selectedAccountIds,
    opportunityOwners: selectedOpportunityOwnerIds,
    ...toArrAmountQuery(selectedArrAmount),
    nextMeetingDateRange:
      nextMeetingRange?.startDate && nextMeetingRange?.endDate
        ? {
            startTime: new Date(nextMeetingRange.startDate).toISOString(),
            endTime: new Date(nextMeetingRange.endDate).toISOString(),
          }
        : undefined,
  }, isDealStageReady);

  const {
    isError: isOpportunitiesError,
    error: opportunitiesError,
    data: opportunitiesData,
  } = UserInteractionsService.useGetActiveOpportunities(
    true,
    isImpersonating && impersonatingUserPorId
      ? {
          impersonatingPorId: impersonatingUserPorId,
          accessLevel: accessLevel,
        }
      : undefined
  );

  useEffect(() => {
    if (opportunitiesData) {
      const payload = opportunitiesData?.data ?? opportunitiesData;
      setOpportunityOptions(
        toOpportunityFilterOptions(Array.isArray(payload?.opportunities) ? payload.opportunities : [])
      );
      setAccountOptions(toRecordFilterOptions(payload?.subOrgs));
      setOpportunityOwnerOptions(toRecordFilterOptions(payload?.opportunityOwners));
      const rawStages = Array.isArray(payload?.dealStages) ? payload.dealStages : [];
      const dealOptions = toFilterOptions(rawStages);
      setDealStageOptions(dealOptions);
      if (!isDealStageInitialized.current) {
        if (dealOptions.length > 0) {
          isDealStageInitialized.current = true;
          if (!navState?.selectedDealStages) {
            const openStages = dealOptions
              .map((opt) => opt.value)
              .filter((val) => !CLOSED_DEAL_STAGES.has(val.trim().toLowerCase()));
            setSelectedDealStages(openStages);
          }
        }
        setIsDealStageReady(true);
      }
      setManagerForecastOptions(
        toFilterOptions(Array.isArray(payload?.managerForecasts) ? payload.managerForecasts : [])
      );
    } else if (isOpportunitiesError) {
      setIsDealStageReady(true);
      showSnackbar(
        "error",
        opportunitiesError?.response?.data?.message ||
          opportunitiesError?.message ||
          "Failed to load opportunities data."
      );
    }
  }, [opportunitiesData, isOpportunitiesError, opportunitiesError, navState?.selectedDealStages]);

  useEffect(() => {
    if (isSubOrgOpportunitiesSuccess && subOrgOpportunitiesData) {
      const list = Array.isArray(subOrgOpportunitiesData?.data)
        ? subOrgOpportunitiesData.data
        : [];
      setRows(generateRows(list));
      setTotalRecords(subOrgOpportunitiesData?.totalRecords ?? list.length);
    }
    if (isSubOrgOpportunitiesError) {
      setRows([]);
      setTotalRecords(0);
      showSnackbar(
        "error",
        subOrgOpportunitiesError?.response?.data?.message ||
          subOrgOpportunitiesError?.message ||
          "Failed to load opportunities."
      );
    }
  }, [
    isSubOrgOpportunitiesSuccess,
    isSubOrgOpportunitiesError,
    subOrgOpportunitiesError,
    subOrgOpportunitiesData,
  ]);

  const defaultColumnVisibility: OpportunityColumnVisibility = {
    opportunityName: true,
    account: true,
    arrAmount: true,
    opportunityOwner: true,
    dealStage: true,
    closeDate: true,
    nextMeeting: true,
    managerForecast: true,
  };
  const [columnVisibility, setColumnVisibility] = useState<OpportunityColumnVisibility>(() => {
    try {
      const saved = sessionStorage.getItem("opportunityColumnVisibility");
      if (!saved) return defaultColumnVisibility;
      const parsed = JSON.parse(saved);
      return {
        opportunityName: parsed.opportunityName ?? defaultColumnVisibility.opportunityName,
        account: parsed.account ?? parsed.accountName ?? defaultColumnVisibility.account,
        arrAmount: parsed.arrAmount ?? parsed.arrAmmount ?? defaultColumnVisibility.arrAmount,
        opportunityOwner: parsed.opportunityOwner ?? defaultColumnVisibility.opportunityOwner,
        dealStage: parsed.dealStage ?? defaultColumnVisibility.dealStage,
        closeDate: parsed.closeDate ?? parsed.closedDate ?? defaultColumnVisibility.closeDate,
        nextMeeting: parsed.nextMeeting ?? defaultColumnVisibility.nextMeeting,
        managerForecast: parsed.managerForecast ?? defaultColumnVisibility.managerForecast,
      };
    } catch {
      return defaultColumnVisibility;
    }
  });

  useEffect(() => {
    if (!isAccountSlideOver) {
      sessionStorage.setItem("opportunityColumnVisibility", JSON.stringify(columnVisibility));
    }
  }, [columnVisibility, isAccountSlideOver]);

  const updateColumnFilter = (key: string, value: string[] | DateRangeValue | NumberFilterValue | undefined) => {
    if (key === "opportunityName") setSelectedOpportunityIds(Array.isArray(value) ? value : []);
    else if (key === "account") setSelectedAccountIds(Array.isArray(value) ? value : []);
    else if (key === "opportunityOwner") setSelectedOpportunityOwnerIds(Array.isArray(value) ? value : []);
    else if (key === "dealStage") {
      isDealStageInitialized.current = true;
      setSelectedDealStages(Array.isArray(value) ? value : []);
    }
    else if (key === "managerForecast") setSelectedManagerForecasts(Array.isArray(value) ? value : []);
    else if (key === "arrAmount") {
      if (value && !Array.isArray(value) && "operator" in value && "value" in value) {
        setSelectedArrAmount(value);
      } else {
        setSelectedArrAmount(undefined);
      }
    } else if (key === "nextMeeting") {
      if (
        value &&
        !Array.isArray(value) &&
        "startDate" in value &&
        "endDate" in value &&
        value.startDate &&
        value.endDate
      ) {
        setNextMeetingRange(value);
      } else {
        setNextMeetingRange(null);
      }
    } else if (key === "closeDate") {
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        "startDate" in value &&
        "endDate" in value &&
        value.startDate &&
        value.endDate
      ) {
        setCloseDateRange(value as DateRangeValue);
      } else {
        setCloseDateRange(null);
      }
    }
    setPageNumber(1);
  };

  const [selectedOpportunity, setSelectedOpportunity] = useState<OpportunityRow | null>(() => navState?.selectedOpportunity || null);
  const [isSlideOverOpen, setIsSlideOverOpen] = useState<boolean>(() => Boolean(navState?.isSlideOverOpen && navState?.selectedOpportunity));

  useEffect(() => {
    const incomingNavState = isAccountSlideOver
      ? accountViewState?.opportunityNavigationState
      : location.state?.opportunityNavigationState;

    if (incomingNavState?.selectedOpportunity && incomingNavState?.isSlideOverOpen) {
      setSelectedOpportunity(incomingNavState.selectedOpportunity);
      setIsSlideOverOpen(true);
      setNavState(incomingNavState);
    }
  }, [
    isAccountSlideOver,
    accountViewState?.opportunityNavigationState,
    location.state?.opportunityNavigationState,
    location.key,
  ]);

  const handleOpportunityClick = (row: any) => {
    setSelectedOpportunity(row);
    setIsSlideOverOpen(true);
    // Clear any past snapshot details so the newly selected opportunity starts clean:
    setNavState((prev) => (prev ? {
    ...prev,
    selectedOpportunity: row,
    detailsDateRange: undefined,
    detailsPageNumber: 1,
    detailsPageSize: 50,
    detailsSort: undefined,
  } : null));
  };

  const effectiveParentPageIdentifier = accountViewState?.parentPageIdentifier || "opportunities";

  const sanitizedAccountViewState = useMemo(() => {
    const state = accountViewState || location.state?.accountViewState;

    if (!state) return undefined;

    const copy = { ...state };
    delete copy.opportunityNavigationState;

    return copy;
  }, [accountViewState, location.state?.accountViewState]);

  const opportunityNavigationState: OpportunityNavigationState = useMemo(() => ({
    parent_page_identifier: effectiveParentPageIdentifier,
    ParentPageIdentifier: effectiveParentPageIdentifier,
    isAccountSlideOver,
    activeView: isAccountSlideOver ? "opportunity" : undefined,
    accountViewState: sanitizedAccountViewState,
    salesforceAccountId: sf_account_id || sanitizedAccountViewState?.salesforceAccountId,
    salesforceAccountUrl: sanitizedAccountViewState?.salesforceAccountUrl,

    dateRange,
    selectedOpportunityIds,
    selectedAccountIds,
    selectedOpportunityOwnerIds,
    selectedDealStages,
    selectedManagerForecasts,
    selectedArrAmount,
    nextMeetingRange,
    closeDateRange,
    sort,
    pageNumber,
    pageSize,
    columnVisibility,
    isSlideOverOpen,
    selectedOpportunity,
    detailsDateRange: navState?.detailsDateRange,
    detailsPageNumber: navState?.detailsPageNumber,
    detailsPageSize: navState?.detailsPageSize,
    detailsSort: navState?.detailsSort,
  }), [
    effectiveParentPageIdentifier,
    isAccountSlideOver,
    sanitizedAccountViewState,
    dateRange,
    selectedOpportunityIds,
    selectedAccountIds,
    selectedOpportunityOwnerIds,
    selectedDealStages,
    selectedManagerForecasts,
    selectedArrAmount,
    nextMeetingRange,
    closeDateRange,
    sort,
    pageNumber,
    pageSize,
    columnVisibility,
    isSlideOverOpen,
    selectedOpportunity,
    navState?.detailsDateRange,
    navState?.detailsPageNumber,
    navState?.detailsPageSize,
    navState?.detailsSort,
  ]);

  const handleCloseSlideOver = () => {
    setIsSlideOverOpen(false);
    setSelectedOpportunity(null);
    setNavState(undefined);

    if (accountViewState) {
      accountViewState.opportunityNavigationState = undefined;
    }

    const locState = location.state as any;
    if (
      locState?.opportunityNavigationState ||
      locState?.accountViewState?.opportunityNavigationState
    ) {
      const cleanState = { ...locState };
      delete cleanState.opportunityNavigationState;
      if (cleanState.accountViewState) {
        cleanState.accountViewState = { ...cleanState.accountViewState };
        delete cleanState.accountViewState.opportunityNavigationState;
      }
      navigate(location.pathname, { replace: true, state: cleanState });
    }
  };

  const columns = useMemo(
    () =>
      getOpportunitiesColumns({
        opportunityOptions,
        accountOptions,
        opportunityOwnerOptions,
        dealStageOptions,
        managerForecastOptions,
        columnFilters: {
          opportunityName: selectedOpportunityIds,
          account: selectedAccountIds,
          opportunityOwner: selectedOpportunityOwnerIds,
          dealStage: selectedDealStages,
          managerForecast: selectedManagerForecasts,
          ...(selectedArrAmount?.operator && selectedArrAmount.value !== undefined
            ? { arrAmount: selectedArrAmount }
            : {}),
          ...(nextMeetingRange?.startDate && nextMeetingRange?.endDate
            ? { nextMeeting: nextMeetingRange }
            : {}),
          ...(closeDateRange?.startDate && closeDateRange?.endDate
            ? { closeDate: closeDateRange }
            : {}),
        },
        updateColumnFilter,
        openFilterColumn,
        setOpenFilterColumn,
        hideAccountFilter: isAccountSlideOver,
        isAccountSlideOver,
      }, handleOpportunityClick),
    [
      opportunityOptions,
      accountOptions,
      opportunityOwnerOptions,
      dealStageOptions,
      managerForecastOptions,
      selectedOpportunityIds,
      selectedAccountIds,
      selectedOpportunityOwnerIds,
      selectedDealStages,
      selectedManagerForecasts,
      selectedArrAmount,
      nextMeetingRange,
      closeDateRange,
      openFilterColumn,
      isAccountSlideOver,
    ]
  );
  const allColumnsVisible: OpportunityColumnVisibility = useMemo(
    () => ({
      opportunityName: true,
      account: false,
      arrAmount: true,
      opportunityOwner: true,
      dealStage: true,
      closeDate: true,
      nextMeeting: true,
      managerForecast: true,
    }),
    []
  );

  const tableColumnVisibility = useMemo(
    () => (isAccountSlideOver ? allColumnsVisible : columnVisibility),
    [allColumnsVisible, columnVisibility, isAccountSlideOver]
  );

  const hasColumnFilterPills =
    selectedOpportunityIds.length > 0 ||
    (!isAccountSlideOver && selectedAccountIds.length > 0) ||
    selectedOpportunityOwnerIds.length > 0 ||
    selectedDealStages.length > 0 ||
    selectedManagerForecasts.length > 0 ||
    Boolean(selectedArrAmount?.operator && selectedArrAmount.value !== undefined) ||
    Boolean(nextMeetingRange?.startDate && nextMeetingRange?.endDate) ||
    Boolean(isAccountSlideOver && closeDateRange?.startDate && closeDateRange?.endDate);

  return (
    <ExclusiveColumnFilterContext.Provider value={exclusiveFilterValue}>
    <Box sx={{width: "100%", height: "100%"}}>
      {(!isAccountSlideOver || hasColumnFilterPills) && (
        <Box className="account-page-filter-box">
        <Box className="account-page-filter-section">
          <Box className="account-page-miltiple-filter">
            {!isAccountSlideOver && (
            <FilterPill
              key="meeting-date"
              type="date"
              label="Closed Date"
              value={dateRange}
              displayValue={formatDateRange(
                  dateRange.startDate,
                  dateRange.endDate
              )}
              showClearIcon={false}
              datePresetOptions={[
              { label: "Current Week", value: "currentWeek" },
              { label: "Current Month", value: "currentMonth" },
              { label: "Last Week", value: "lastWeek" },
              { label: "Last Month", value: "lastMonth" },
              { label: "Custom", value: "custom" },
              ]}
              onChange={(val) => {
              if (
                  val &&
                  typeof val === "object" &&
                  !Array.isArray(val) &&
                  "preset" in val
              ) {
                  if (val.preset === "last3MonthsFull") {
                  const range = getPresetDateRange("last3MonthsFull");

                  const updated = {
                      preset: "last3MonthsFull",
                      startDate: range.start_time,
                      endDate: range.end_time,
                  };

                  setDateRange(updated);
                  setOpportunityDateRange?.(updated);
                  setPageNumber(1);
                  return;
                  }

                  setDateRange(val);
                  setOpportunityDateRange?.(val);
                  setPageNumber(1);
              }
              }}
            />
            )}
            {[
              {
                label: "Opportunity Name",
                columnKey: "opportunityName",
                options: opportunityOptions,
                value: selectedOpportunityIds,
                setValue: setSelectedOpportunityIds,
                hideUntilSearchThreshold: 5,
              },
              {
                label: "Account",
                columnKey: "account",
                options: accountOptions,
                value: selectedAccountIds,
                setValue: setSelectedAccountIds,
              },
              {
                label: "Opp. Owner",
                columnKey: "opportunityOwner",
                options: opportunityOwnerOptions,
                value: selectedOpportunityOwnerIds,
                setValue: setSelectedOpportunityOwnerIds,
              },
              {
                label: "Deal Stage",
                columnKey: "dealStage",
                options: dealStageOptions,
                value: selectedDealStages,
                setValue: (val: string[]) => {
                  isDealStageInitialized.current = true;
                  setSelectedDealStages(val);
                },
              },
              {
                label: "Manager Forecast",
                columnKey: "managerForecast",
                options: managerForecastOptions,
                value: selectedManagerForecasts,
                setValue: setSelectedManagerForecasts,
              },
            ]
              .filter((pill) => pill.value.length > 0)
              .filter((pill) => !(isAccountSlideOver && pill.columnKey === "account"))
              .map((pill) => (
                <FilterPill
                  key={pill.columnKey}
                  label={pill.label}
                  columnKey={pill.columnKey}
                  options={pill.options}
                  value={pill.value}
                  hideUntilSearchThreshold={pill.hideUntilSearchThreshold}
                  parent_page={pill.columnKey === "account" ? "opportunities-page" : undefined}
                  onChange={(val) => {
                    if (Array.isArray(val)) pill.setValue(val);
                    else if (typeof val === "string") pill.setValue(val ? [val] : []);
                    else pill.setValue([]);
                    setPageNumber(1);
                  }}
                  showClearIcon
                  useColumnFilter
                  isEnhancedLabel={true}
                />
              ))}
            {selectedArrAmount?.operator && selectedArrAmount.value !== undefined && (
              <FilterPill
                key="arrAmount"
                type="number"
                label="ARR Amount"
                columnKey="arrAmount"
                value={selectedArrAmount}
                displayValue={`${selectedArrAmount.operator === "gt" ? ">" : "<"} $${selectedArrAmount.value.toLocaleString("en-US")}`}
                showClearIcon
                useColumnFilter
                numberPlaceholder="Insert ARR"
                onChange={(val) => {
                  if (
                    val &&
                    typeof val === "object" &&
                    !Array.isArray(val) &&
                    "operator" in val &&
                    "value" in val
                  ) {
                    setSelectedArrAmount(val as NumberFilterValue);
                  } else {
                    setSelectedArrAmount(undefined);
                  }
                  setPageNumber(1);
                }}
              />
            )}
            {nextMeetingRange?.startDate && nextMeetingRange?.endDate && (
              <FilterPill
                key="nextMeeting"
                type="date"
                label="Next Meeting"
                columnKey="nextMeeting"
                value={nextMeetingRange}
                displayValue={formatDateRange(
                  nextMeetingRange.startDate,
                  nextMeetingRange.endDate
                )}
                showClearIcon
                useColumnFilter
                onChange={(val) => {
                  if (
                    val &&
                    typeof val === "object" &&
                    !Array.isArray(val) &&
                    "startDate" in val &&
                    "endDate" in val &&
                    val.startDate &&
                    val.endDate
                  ) {
                    setNextMeetingRange(val);
                  } else {
                    setNextMeetingRange(null);
                  }
                  setPageNumber(1);
                }}
              />
            )}
            {isAccountSlideOver && closeDateRange?.startDate && closeDateRange?.endDate && (
              <FilterPill
                key="closeDate"
                type="date"
                label="Closed Date"
                columnKey="closeDate"
                value={closeDateRange}
                displayValue={formatDateRange(
                  closeDateRange.startDate,
                  closeDateRange.endDate
                )}
                showClearIcon
                useColumnFilter
                onChange={(val) => {
                  if (
                    val &&
                    typeof val === "object" &&
                    !Array.isArray(val) &&
                    "startDate" in val &&
                    "endDate" in val &&
                    val.startDate &&
                    val.endDate
                  ) {
                    setCloseDateRange(val as DateRangeValue);
                  } else {
                    setCloseDateRange(null);
                  }
                  setPageNumber(1);
                }}
              />
            )}
          </Box>
        </Box>
        {!isAccountSlideOver && (
        <Box className="account-page-right-side-filters">
          <UserTeam
            setSelectedUser={setSelectedUser}
            loggedInEmail={loggedInEmail}
            loggedInProfileOrgId={loggedInProfileOrgId || impersonatingEmployeeId}
            selectedEmployeePorId={ selectedUser?.profileOrgId}
            showDropdown={true}
            display={false}
            onDataUpdate={handleNewUserTeamData}
            parent_page="opportunities-page"
          />
          <ColumnFilter
            currentValue={columnVisibility}
            onApply={(val) => setColumnVisibility((prev) => ({ ...prev, ...(val as OpportunityColumnVisibility) }))}
            options={columnPickerOptions}
            type="toggle"
            columnKey="columnVisibility"
            onOpen={() => setOpenFilterColumn("columnVisibility")}
            onClose={() => setOpenFilterColumn(null)}
            isActive={openFilterColumn === "columnVisibility"}
            showSections={{
              search: true,
              sort: false,
            }}
          />
        </Box>
        )}
      </Box>
      )}
      <Box
        sx={{
          border: isAccountSlideOver ? "none" : "1px solid #E5E7EB",
          borderTop: isAccountSlideOver ? "1px solid #C2C2CA" : "1px solid #E5E7EB",
          borderRadius: isAccountSlideOver ? 0 : "8px",
          overflow: "hidden",
          marginTop: "12px",
        }}
      >
        <Datatable
          columns={columns}
          rows={rows}
          rowCount={totalRecords}
          showSpinner={!isDealStageReady || isSubOrgOpportunitiesLoading || isSubOrgOpportunitiesFetching}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPageNumber(1);
          }}
          onPaginationChange={(newPage) => setPageNumber(newPage + 1)}
          currentPageIndex={pageNumber - 1}
          onSortingChange={onSortingChange}
          minHeight={
            isAccountSlideOver
              ? "calc(100vh - 315px)"
              : isImpersonating
                ? "calc(100vh - 260px)"
                : "calc(100vh - 230px)"
          }
          maxHeight={
            isAccountSlideOver
              ? "calc(100vh - 315px)"
              : isImpersonating
                ? "calc(100vh - 260px)"
                : "calc(100vh - 230px)"
          }
          columnVisibility={tableColumnVisibility}
          timeRange=""
          calendarId={null}
          pinnedColumns={{ left: ["opportunityName"] }}
          initialSort={sort}
          headerRow={40}
          bodyRow={46}
          tablePadding= {isAccountSlideOver ? "0px" : "0px 10px 10px 10px"}
          rowHoverEnabled
        />
      </Box>
      <SlideOver
        isOpen={isSlideOverOpen}
        onClose={handleCloseSlideOver}
        padding="0px"
      >
        {isSlideOverOpen && (
          <OpportunityDetails
            opportunity={selectedOpportunity}
            parentDateRange={
              externalDateRange?.startDate && externalDateRange?.endDate
                ? {
                    startDate: externalDateRange.startDate,
                    endDate: externalDateRange.endDate,
                    preset: "custom",
                  }
                : dateRange
            }
            navigationState={opportunityNavigationState}
          />
        )}
      </SlideOver>
    </Box>
    </ExclusiveColumnFilterContext.Provider>
  );

};

export default Opportunities;
